import type { SimplifiedCookieAttributes as CookieAttributes } from './types/cookie';
import {
  CURRENT_SESSION_STARTED_AT_STORAGE_KEY,
  DEFAULT_INTERVAL_DAYS_TO_STORAGE_EXPIRATION,
  IS_FIRST_SESSION_STORAGE_KEY,
  LAST_EVENT_TRACKED_AT_STORAGE_KEY,
  DEFAULT_SESSION_EXPIRY_INTERVAL,
  USER_ID_STORAGE_KEY,
} from './constants';
import { storage } from './utility/storage';
import uuid from './utility/uuid';
import { console } from './utility/console';
import { isValidCookieDomain } from './utility/metadata';

type FirstSessionValue = { [idfv: string]: 0 | 1 };

const IS_FIRST_VISIT = {
  true: 1,
  false: 0,
};

const defaultParams = {
  idfv: uuid(),
  intervalDaysToStorageExpiration: DEFAULT_INTERVAL_DAYS_TO_STORAGE_EXPIRATION,
  sessionExpiryInterval: DEFAULT_SESSION_EXPIRY_INTERVAL,
  cookieDomain: undefined,
};

interface Params {
  idfv: string;
  currentDate?: Date;
  firstSessionValue?: any;
  lastEventTrackedAt?: Date;
  intervalDaysToStorageExpiration?: number;
  sessionExpiryInterval?: number;
  cookieDomain?: string;
}

// 厳密に言うと「初回セッション(初回訪問)」という概念は BrowserSession と切り離したほうがよさそうではあるが、収まりのよいところがここしかなかった
export class BrowserSession {
  private idfv: string;
  private firstSessionValue: FirstSessionValue;
  private lastEventTrackedAt?: Date;
  public intervalDaysToStorageExpiration: number;
  private sessionStartedAt?: Date; // start() のコメント欄もあわせて参照
  public sessionExpiryInterval: number;
  private cookieOptions?: CookieAttributes;

  // TODO: 最終的には intervalDaysToStorageExpiration ではなくオブジェクトとしての storage を引数に渡すようにしたい(import 元の storage に記載したコメントも参照)
  public constructor({
    idfv,
    currentDate,
    firstSessionValue,
    lastEventTrackedAt,
    intervalDaysToStorageExpiration,
    sessionExpiryInterval,
    cookieDomain,
  }: Params = defaultParams) {
    this.idfv = idfv;
    this.lastEventTrackedAt = lastEventTrackedAt;
    this.intervalDaysToStorageExpiration =
      intervalDaysToStorageExpiration || defaultParams.intervalDaysToStorageExpiration;

    if (cookieDomain) {
      if (!isValidCookieDomain(cookieDomain)) {
        console.error(`Invalid cookie_domain value: ${cookieDomain}`);
      } else {
        this.cookieOptions = { domain: cookieDomain };
      }
    }

    if (typeof sessionExpiryInterval !== 'number') {
      // session_expiry_interval が指定されていない、あるいは Number 以外の値のときはデフォルト値を利用
      if (sessionExpiryInterval !== undefined) {
        console.warn('session_expiry_interval option must be an integer.');
      }
      this.sessionExpiryInterval = defaultParams.sessionExpiryInterval;
    } else if (sessionExpiryInterval <= 0) {
      // 0または負の整数が与えられたときは即時反映を意図しているものと判断し 0 をセットする
      this.sessionExpiryInterval = 0;
    } else {
      // それ以外の場合では指定された値をセットする
      this.sessionExpiryInterval = sessionExpiryInterval;
    }

    this.firstSessionValue = this.migratedSession({ originalValue: firstSessionValue, currentDate });
  }

  public isContinuous(date?: Date): boolean {
    if (!date || !this.lastEventTrackedAt) {
      return false;
    }

    return date.getTime() <= this.lastEventTrackedAt.getTime() + this.sessionExpiryInterval;
  }

  public isFirstVisit(): boolean {
    return this.firstSessionValue[this.idfv] === IS_FIRST_VISIT.true;
  }

  public extendExpiration(date: Date): void {
    if (!this.lastEventTrackedAt || this.lastEventTrackedAt < date) this.lastEventTrackedAt = date;
  }

  // storage に保存された内容と差分がない場合だけ更新する選択肢もあるが、simple さを優先して常時上書きとする
  public save(): void {
    const options = {
      expires: this.intervalDaysToStorageExpiration as number,
      ...(this.cookieOptions || {}),
    };

    storage.set(USER_ID_STORAGE_KEY, this.idfv, options);

    // save() 時点での初回セッション状態に更新すべきではという気持ちが湧いたがいったん保留
    storage.set(IS_FIRST_SESSION_STORAGE_KEY, this.firstSessionValue, options);

    if (this.lastEventTrackedAt) {
      storage.set(LAST_EVENT_TRACKED_AT_STORAGE_KEY, this.lastEventTrackedAt.getTime().toString(), options);
    }

    if (this.sessionStartedAt) {
      storage.set(CURRENT_SESSION_STARTED_AT_STORAGE_KEY, this.sessionStartedAt.getTime().toString(), options);
    }
  }

  public revoke(): void {
    this.lastEventTrackedAt = undefined;
    storage.remove(LAST_EVENT_TRACKED_AT_STORAGE_KEY);
  }

  // 標準ユーザープロフィール設定で使用するものであり(ref: #205)、セッション状態(初回 or NOT, 継続中 or NOT)とは無関係
  // とはいえほかに置き場所がないため、やむを得ず本クラスにメソッドを生やしている
  public start(date: Date): void {
    this.sessionStartedAt = date;
  }

  private migratedSession({
    originalValue,
    currentDate,
  }: {
    originalValue: any;
    currentDate?: Date;
  }): FirstSessionValue {
    const { idfv } = this;

    if (originalValue == null) {
      return { [idfv]: IS_FIRST_VISIT.true } as FirstSessionValue;
    }

    const normalized = (value: 0 | 1 | undefined) => {
      switch (value) {
        case IS_FIRST_VISIT.false:
          return { [idfv]: IS_FIRST_VISIT.false };
        case IS_FIRST_VISIT.true:
          return {
            [idfv]: this.isContinuous(currentDate) ? IS_FIRST_VISIT.true : IS_FIRST_VISIT.false,
          };
        case undefined:
          return { [idfv]: IS_FIRST_VISIT.true };
        default:
          throw new Error(`Unexpected value has been passed: "${value}"`);
      }
    };

    if (typeof originalValue !== 'object') throw new Error(`Invalid value has been passed: "${originalValue}"`);

    // idfv に加えて user_annotation も用いて初回セッション状態を判定していた頃の名残を解消するためのマイグレーションコード(ref. https://repro.esa.io/posts/20557)
    // TODO: マルチテナント対応時にこのコードを消す(ref. #253, #267)
    const includesZeroValueOfNonIdfvKey = () => {
      const keys = Object.keys(originalValue);
      return keys.some((key) => key !== idfv && originalValue[key] === IS_FIRST_VISIT.false);
    };
    // TODO ここまで

    return {
      ...originalValue,
      ...normalized(includesZeroValueOfNonIdfvKey() ? IS_FIRST_VISIT.false : originalValue[idfv]),
    };
  }

  public static migrateIdfv(
    oldIdfv: string,
    newIdfv: string,
    options: { intervalDaysToStorageExpiration: number; cookieDomain: string | undefined }
  ): void {
    storage.set(USER_ID_STORAGE_KEY, newIdfv, options);

    const oldValue = storage.get(IS_FIRST_SESSION_STORAGE_KEY, { asJSON: true }) as Record<string, FirstSessionValue>;
    const newValue = {} as Record<string, FirstSessionValue>;
    Object.entries(oldValue).forEach(([k, v]) => {
      if (k === oldIdfv) {
        newValue[newIdfv] = v;
      } else {
        newValue[k] = v;
      }
    });

    storage.set(IS_FIRST_SESSION_STORAGE_KEY, newValue, options);
  }
}
